import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Chip, Avatar, Stack, Divider, CircularProgress } from '@mui/material';
import { BusinessCenter, AccessTime, CalendarMonth, Assignment, Description, Person, Groups, Domain, Code } from "@mui/icons-material";
import { getStatusColor, priorityColors } from "../../../utils/statusColor";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import { formatDate } from "../../../utils/dateFormat";

function MyProjectDetails({ project }) {
    const [projectData, setProjectData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const targetProjectId = project?.id;
        if (targetProjectId) {
            setIsLoading(true);
            getData(API_ENDPOINTS.fetchProjectById(targetProjectId))
                .then((res) => {
                    setProjectData(res?.message?.data || null);
                })
                .finally(() => setIsLoading(false));
        }
    }, [project]);

    if (isLoading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!projectData) return <Typography sx={{ p: 3 }}>No details found.</Typography>;

    const { project: details, manager, team_leader, departments, clients } = projectData;
    const statusStyles = getStatusColor(details?.status);
    const priorityStyles = priorityColors[details?.priority] || priorityColors.Low;
    const clientName = clients?.[0] ? `${clients[0].first_name} ${clients[0].last_name}` : "N/A";

    const InfoRow = ({ icon: Icon, label, value, customValue }) => (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
            <Avatar sx={{ bgcolor: '#F1F5F9', color: '#475569', width: 34, height: 34, borderRadius: '8px' }}>
                <Icon sx={{ fontSize: 18 }} />
            </Avatar>
            <Stack direction="column" spacing={0.5}>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                </Typography>
                <Box>
                    {customValue ? customValue : (
                        <Typography variant="body1" sx={{ color: '#0F172A', fontWeight: 700 }}>
                            {value || 'N/A'}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Box>
    );

    return (
        <Box sx={{ p: 2, bgcolor: '#ededed' }}>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }} sx={{ borderRight: { md: '1px solid #f1f5f9' } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#0F172A', width: 32, height: 32, borderRadius: '8px' }}>
                            <BusinessCenter sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 800, color: '#0F172A' }}>PROJECT DETAILS</Typography>
                    </Stack>
                    <Divider sx={{ mb: 3 }} />

                    <InfoRow icon={BusinessCenter} label="Project Name" value={details?.project_title} />
                    <InfoRow icon={AccessTime} label="Priority" customValue={
                        <Chip
                            label={details?.priority}
                            size="small"
                            sx={{
                                bgcolor: priorityStyles.bg,
                                color: priorityStyles.color,
                                fontWeight: 800,
                                borderRadius: '24px',
                                px: 1,
                                border: `1px solid ${priorityStyles.border || '#e2e8f0'}`
                            }}
                        />
                    } />
                    <InfoRow icon={CalendarMonth} label="Timeline" value={`${formatDate(details?.start_date)} - ${formatDate(details?.end_date)}`} />
                    <InfoRow icon={Assignment} label="Current Status" customValue={
                        <Chip
                            label={details?.status}
                            size="small"
                            sx={{
                                bgcolor: statusStyles.bg,
                                color: statusStyles.color,
                                fontWeight: 800,
                                borderRadius: '24px',
                                px: 1,
                                border: `1px solid ${statusStyles.border || '#e2e8f0'}`
                            }}
                        />
                    } />
                    <InfoRow icon={Description} label="Description" value={details?.description} />
                    <InfoRow icon={Person} label="Client" value={clientName} />
                </Grid>

                {/* RIGHT COLUMN: Team Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#10B981', width: 32, height: 32, borderRadius: '8px' }}>
                            <Groups sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 800, color: '#0F172A' }}>TEAM ARCHITECTURE</Typography>
                    </Stack>
                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={2} sx={{ mb: 4 }}>
                        {[
                            { label: "Project Manager", user: manager, color: '#0F172A' },
                            { label: "Technical Lead", user: team_leader, color: '#10B981' }
                        ].map((role, idx) => (
                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#F8FAFC', borderRadius: '12px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748B' }}>{role.label}</Typography>
                                <Chip
                                    avatar={<Avatar sx={{ bgcolor: role.color, color: '#FFFFFF !important', textTransform: 'uppercase' }}>{role.user?.first_name?.[0]}</Avatar>}
                                    label={`${role.user?.first_name || 'N/A'} ${role.user?.last_name || ''}`}
                                    sx={{ fontWeight: 700, bgcolor: '#fff', border: '1px solid #E2E8F0' }}
                                />
                            </Box>
                        ))}
                    </Stack>

                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94A3B8', mb: 2, display: 'block', letterSpacing: '0.1em' }}>DEPARTMENTS & TEAMS</Typography>

                    <Stack spacing={2.5}>
                        {departments?.map((dept) => (
                            <Box key={dept.department_id} sx={{ p: 2, borderRadius: '16px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                                {/* Department Header */}
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Avatar sx={{ bgcolor: '#F1F5F9', color: '#0F172A', borderRadius: '8px', width: 36, height: 36, border: '1px solid #E2E8F0' }}>
                                        <Domain sx={{ fontSize: 20 }} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800, textTransform: 'uppercase' }}>Department</Typography>
                                        <Typography variant="body1" sx={{ color: '#0F172A', fontWeight: 800 }}>{dept.department_name}</Typography>
                                    </Box>
                                </Stack>

                                <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                                {/* Teams Loop - FIXED: Removed the extra department map here */}
                                <Stack spacing={2}>
                                    {dept.teams?.map((team) => (
                                        <Box key={team.team_id} sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #F1F5F9' }}>
                                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Code sx={{ fontSize: 16, color: '#10B981' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>{team.team_name}</Typography>
                                                </Stack>
                                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8' }}>{team.members?.length} MEMBERS</Typography>
                                            </Stack>

                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {team.members?.map((member) => (
                                                    <Chip
                                                        key={member.id}
                                                        size="small"
                                                        label={`${member.first_name} ${member.last_name}`}
                                                        sx={{
                                                            bgcolor: '#FFF',
                                                            border: '1px solid #E2E8F0',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            '&:hover': { bgcolor: '#0F172A', color: '#FFF', borderColor: '#7993d1' },
                                                            transition: '0.2s',
                                                            px: 1
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

export default MyProjectDetails;